/**
 * Message grouping utility for inline message rendering
 *
 * Groups harness messages into:
 * - TextGroup: Assistant text messages (rendered as File components)
 * - ToolGroup: Paired tool_use + tool_result (rendered as expandable tabs)
 * - EditGroup: Edit tool calls (rendered as diffs)
 * - BashGroup: Bash commands (rendered as prompt/response)
 * - StderrGroup: stderr output from harness process
 *
 * Delegates to per-harness parsers (e.g. claudeCodeParser) based on harnessId.
 */

import type { ReactNode } from "react"
import type { HarnessId, HarnessRawMessageEvent, HarnessStreamEvent, HarnessUsage } from "../../providerEvents/harnessEventTypes"
import { extractRawMessageEvents } from "../../providerEvents/harnessEventTypes"
import { groupClaudeCodeProviderEvents } from "./parsers/claudeCodeParser"
import { groupCodexProviderEvents } from "./parsers/codexParser"
import { groupGrokProviderEvents } from "./parsers/grokParser"
import { groupOpenCodeProviderEvents } from "./parsers/openCodeParser"

// ============================================================================
// Render Mode Types
// ============================================================================

export type RenderMode = "inline" | "row" | "pill"

export interface DisplayContext {
    isLatestTodoGroup: boolean
}

export interface CommentContext {
    taskId: string
    actionEventId: string
}

export interface GroupRenderer<T> {
    getLabel(group: T): string
    getIcon(group: T): ReactNode
    getStatusIcon?(group: T): ReactNode | null
    getHeaderInfo?(group: T): ReactNode | null
    renderContent(group: T, ctx: CommentContext): ReactNode
}

export interface GroupWithMeta {
    group: MergedGroup
    mode: RenderMode
    id: string
}

export type RenderableItem =
    | { mode: "inline"; item: GroupWithMeta }
    | { mode: "row"; item: GroupWithMeta }
    | { mode: "pill"; items: GroupWithMeta[] }
    | { mode: "activityDigest"; items: GroupWithMeta[] }

// ============================================================================
// Message Group Types
// ============================================================================

export interface TextGroup {
    type: "text"
    text: string
    messageIndex: number
    endMessageIndex?: number
}

export interface ToolGroup {
    type: "tool"
    toolUseId: string
    toolName: string
    input: unknown
    result?: string
    media?: ToolMedia[]
    isError: boolean
    messageIndices: [number, number | undefined]
}

export interface ToolMedia {
    type: "image"
    url: string
    detail?: string
}

export interface EditGroup {
    type: "edit"
    toolUseId: string
    filePath: string
    oldString: string
    newString: string
    isError: boolean
    isPending: boolean
    errorMessage?: string
    messageIndices: [number, number | undefined]
}

export interface WriteGroup {
    type: "write"
    toolUseId: string
    filePath: string
    content: string
    isError: boolean
    isPending: boolean
    errorMessage?: string
    messageIndices: [number, number | undefined]
}

export interface FileChangeGroup {
    type: "fileChange"
    toolUseId: string
    filePath: string
    kind: string
    status: string
    diff?: string
    isError: boolean
    isPending: boolean
    messageIndex: number
    changeIndex: number
}

export interface BashGroup {
    type: "bash"
    toolUseId: string
    command: string
    description?: string
    result?: string
    sessionId?: number
    isContinuation?: boolean
    durationMs?: number
    isError: boolean
    isPending: boolean
    messageIndices: [number, number | undefined]
}

export interface OrchestrationCall {
    toolName: string
    detail?: string
}

export interface OrchestrationGroup {
    type: "orchestration"
    toolUseId: string
    source: string
    calls: OrchestrationCall[]
    result?: string
    isError: boolean
    isPending: boolean
    messageIndices: [number, number | undefined]
}

export interface SystemGroup {
    type: "system"
    subtype:
        | "compact_boundary"
        | "status"
        | "init"
        | "hook_started"
        | "hook_progress"
        | "hook_response"
        | "api_retry"
        | "task_started"
        | "task_progress"
        | "task_notification"
        | "task_updated"
    metadata: Record<string, unknown>
    messageIndex: number
}

export interface ResultGroup {
    type: "result"
    subtype: "success" | "done_with_gaps" | "error_during_execution" | "error_max_turns" | "error_max_budget_usd" | "error_max_structured_output_retries"
    durationMs: number
    totalCostUsd: number
    usage: {
        inputTokens: number
        outputTokens: number
        cacheReadTokens?: number
        cacheWriteTokens?: number
    }
    isError: boolean
    result?: string
    errors?: string[]
    trailingActivity?: ActivityEvent[]
    messageIndex: number
}

export interface StderrGroup {
    type: "stderr"
    data: string
    eventId: string
}

export interface UnknownGroup {
    type: "unknown"
    harnessId: HarnessId
    label: string
    originalType?: string
    raw: unknown
    messageIndex: number
}

export interface ActivityEvent {
    harnessId: HarnessId
    label: string
    originalType?: string
    summary?: string
    raw: unknown
    messageIndex: number
    count?: number
}

export interface ActivityGroup {
    type: "activity"
    harnessId: HarnessId
    events: ActivityEvent[]
    messageIndex: number
}

export type ProviderNoticeKind =
    | "goal"
    | "rollback"
    | "api_error"
    | "model_fallback"
    | "plan_mode"
    | "plan_mode_exit"
    | "pull_request"
    | "recap"
    | "read_truncation"
    | "agents_killed"
    | "permission_mode"
    | "informational"

export interface ProviderNoticeGroup {
    type: "providerNotice"
    harnessId: HarnessId
    kind: ProviderNoticeKind
    title: string
    body?: string
    url?: string
    level: "info" | "success" | "warning" | "error"
    metadata: Record<string, unknown>
    messageIndex: number
}

export type AgentActivityStatus = "running" | "completed" | "failed" | "killed" | "stopped" | "incomplete" | "unknown"

export interface AgentActivityEvent {
    kind: string
    summary?: string
    timestamp?: string
    raw: unknown
    messageIndex: number
}

export interface AgentActivityUsage {
    totalTokens?: number
    toolUses?: number
    durationMs?: number
}

export interface AgentActivity {
    id: string
    label: string
    status: AgentActivityStatus
    path?: string
    taskType?: string
    model?: string
    summary?: string
    outputFile?: string
    startedAt?: string
    updatedAt?: string
    usage?: AgentActivityUsage
    events: AgentActivityEvent[]
}

export interface AgentActivityGroup {
    type: "agentActivity"
    harnessId: HarnessId
    agents: AgentActivity[]
    isPending: boolean
    hasGaps: boolean
    isError: boolean
    messageIndex: number
}

export interface TodoItem {
    content: string
    status: "pending" | "in_progress" | "completed"
    activeForm: string
}

export interface TodoWriteGroup {
    type: "todoWrite"
    toolUseId: string
    label?: string
    todos: TodoItem[]
    isError: boolean
    isPending: boolean
    messageIndices: [number, number | undefined]
}

export interface ThinkingGroup {
    type: "thinking"
    text: string
    // Peak estimated thinking-token count from the CLI's streaming thinking_tokens telemetry, when present.
    estimatedThinkingTokens?: number
    messageIndex: number
    endMessageIndex?: number
}

export type MessageGroup = (
    | TextGroup
    | ThinkingGroup
    | ToolGroup
    | EditGroup
    | WriteGroup
    | FileChangeGroup
    | BashGroup
    | OrchestrationGroup
    | SystemGroup
    | ResultGroup
    | StderrGroup
    | UnknownGroup
    | ActivityGroup
    | ProviderNoticeGroup
    | AgentActivityGroup
    | TodoWriteGroup
) & { sourceMessageIndex?: number }

// MergedGroup is now just MessageGroup - no tool merging needed
// The groupByRenderMode function handles grouping consecutive pills
export type MergedGroup = MessageGroup

// Stderr lines matching these patterns are internal CLI noise and hidden from the UI
const STDERR_NOISE_PATTERNS = [/codex_core::rollout/]

/**
 * Group unified stream events into message groups for inline rendering
 *
 * Extracts raw messages from events, dispatches to the appropriate
 * per-harness parser, and appends stderr groups.
 */
export function groupStreamEvents(events: HarnessStreamEvent[], harnessId: HarnessId): MessageGroup[] {
    // Extract typed raw message events — discriminated union on harnessId
    const messageEvents = extractRawMessageEvents(events)

    // Extract completion from the harness-level event when provider-native history omits a result.
    const completion = extractCompletion(events)

    // Group by harness using narrowing (no unsafe casts)
    const messageGroups = groupRawMessageEvents(messageEvents, harnessId, completion)

    // Extract stderr events and merge adjacent stderr bursts into a single visible group.
    const stderrGroups = collectStderrGroups(events)

    // Extract harness-level error events (e.g. process_crashed) and render as ResultGroups
    const errorGroups: ResultGroup[] = events
        .filter((e): e is HarnessStreamEvent & { type: "error"; direction: "execution" } => e.direction === "execution" && e.type === "error")
        .map((e) => ({
            type: "result" as const,
            subtype: "error_during_execution" as const,
            durationMs: 0,
            totalCostUsd: 0,
            usage: { inputTokens: 0, outputTokens: 0 },
            isError: true,
            errors: [e.error],
            messageIndex: -1,
        }))

    // Append stderr and errors, then guarantee one terminal summary for a completed turn.
    return finalizeTerminalGroups([...messageGroups, ...stderrGroups, ...errorGroups], completion)
}

/**
 * Add the terminal summary when the surrounding turn lifecycle is complete.
 *
 * Provider message stop reasons are response boundaries, not reliable turn
 * boundaries: Claude can emit `end_turn` while background agents are running.
 */
export function completeMessageGroups(groups: MessageGroup[]): MessageGroup[] {
    return finalizeTerminalGroups(groups, {
        usage: { inputTokens: 0, outputTokens: 0 },
        costUsd: 0,
        durationMs: 0,
    })
}

function collectStderrGroups(events: HarnessStreamEvent[]): StderrGroup[] {
    const groups: StderrGroup[] = []
    let current: StderrGroup | null = null

    for (const event of events) {
        const isVisibleStderr = event.direction === "execution" && event.type === "stderr" && !STDERR_NOISE_PATTERNS.some((pattern) => pattern.test(event.data))

        if (!isVisibleStderr) {
            current = null
            continue
        }

        if (current) {
            current.data += `\n${event.data}`
            continue
        }

        current = {
            type: "stderr",
            data: event.data,
            eventId: event.id,
        }
        groups.push(current)
    }

    return groups
}

/** Dispatch raw message events to per-harness parser using discriminated union narrowing */
function groupRawMessageEvents(events: HarnessRawMessageEvent[], harnessId: HarnessId, completion?: CompletionInfo): MessageGroup[] {
    switch (harnessId) {
        case "claude-code": {
            const messages = events
                .filter((e): e is HarnessRawMessageEvent & { harnessId: "claude-code" } => e.harnessId === "claude-code")
                .map((e) => e.message)
            return groupClaudeCodeProviderEvents(messages)
        }
        case "codex": {
            const messages = events.filter((e): e is HarnessRawMessageEvent & { harnessId: "codex" } => e.harnessId === "codex").map((e) => e.message)
            return groupCodexProviderEvents(messages, completion)
        }
        case "grok-build": {
            const messages = events.filter((e): e is HarnessRawMessageEvent & { harnessId: "grok-build" } => e.harnessId === "grok-build").map((e) => e.message)
            return groupGrokProviderEvents(messages)
        }
        case "opencode": {
            const messages = events.filter((e): e is HarnessRawMessageEvent & { harnessId: "opencode" } => e.harnessId === "opencode").map((e) => e.message)
            return groupOpenCodeProviderEvents(messages)
        }
        default: {
            const _exhaustive: never = harnessId
            return _exhaustive
        }
    }
}

interface CompletionInfo {
    usage: ResultGroup["usage"]
    costUsd: number
    durationMs: number
}

function extractCompletion(events: HarnessStreamEvent[]): CompletionInfo | undefined {
    for (const e of events) {
        if (e.direction !== "execution" || e.type !== "complete") continue
        const usage: HarnessUsage | undefined = e.usage
        return {
            usage: {
                inputTokens: usage?.inputTokens ?? 0,
                outputTokens: usage?.outputTokens ?? 0,
                ...(usage?.cacheReadTokens !== undefined ? { cacheReadTokens: usage.cacheReadTokens } : {}),
                ...(usage?.cacheWriteTokens !== undefined ? { cacheWriteTokens: usage.cacheWriteTokens } : {}),
            },
            costUsd: usage?.costUsd ?? 0,
            durationMs: usage?.durationMs ?? 0,
        }
    }
    return undefined
}

function finalizeTerminalGroups(groups: MessageGroup[], completion?: CompletionInfo): MessageGroup[] {
    const lastTextIndex = groups.findLastIndex((group) => group.type === "text")
    let terminalIndex = groups.findLastIndex((group, index) => group.type === "result" && index > lastTextIndex)

    if (terminalIndex < 0 && !completion) return groups

    let terminal: ResultGroup
    if (terminalIndex >= 0) {
        const existing = groups[terminalIndex]
        if (existing?.type !== "result") return groups
        terminal = completion
            ? {
                  ...existing,
                  durationMs: existing.durationMs || completion.durationMs,
                  totalCostUsd: existing.totalCostUsd || completion.costUsd,
                  usage: mergeCompletionUsage(existing.usage, completion.usage),
              }
            : existing
    } else {
        terminalIndex = groups.length
        terminal = {
            type: "result",
            subtype: "success",
            durationMs: completion?.durationMs ?? 0,
            totalCostUsd: completion?.costUsd ?? 0,
            usage: completion?.usage ?? { inputTokens: 0, outputTokens: 0 },
            isError: false,
            messageIndex: groups.length,
        }
    }

    const trailingActivity: ActivityEvent[] = [...(terminal.trailingActivity ?? [])]
    const remaining = groups.filter((group, index) => {
        if (index === terminalIndex) return false
        if (index > lastTextIndex && group.type === "activity") {
            trailingActivity.push(...group.events)
            return false
        }
        return true
    })

    const settled = remaining.map((group) => (group.type === "agentActivity" ? settleTerminalAgentActivity(group) : group))
    const hasAgentGaps = settled.some((group) => group.type === "agentActivity" && group.hasGaps)
    const honestTerminal =
        terminal.subtype === "success" && hasAgentGaps
            ? {
                  ...terminal,
                  subtype: "done_with_gaps" as const,
              }
            : terminal

    return [...settled, ...(trailingActivity.length > 0 ? [{ ...honestTerminal, trailingActivity }] : [honestTerminal])]
}

function settleTerminalAgentActivity(group: AgentActivityGroup): AgentActivityGroup {
    if (!group.agents.some((agent) => agent.status === "running")) return group
    return {
        ...group,
        agents: group.agents.map((agent) => (agent.status === "running" ? { ...agent, status: "incomplete" as const } : agent)),
        isPending: false,
        hasGaps: true,
    }
}

function mergeCompletionUsage(existing: ResultGroup["usage"], completion: ResultGroup["usage"]): ResultGroup["usage"] {
    const cacheReadTokens = existing.cacheReadTokens ?? completion.cacheReadTokens
    const cacheWriteTokens = existing.cacheWriteTokens ?? completion.cacheWriteTokens
    return {
        inputTokens: existing.inputTokens || completion.inputTokens,
        outputTokens: existing.outputTokens || completion.outputTokens,
        ...(cacheReadTokens !== undefined ? { cacheReadTokens } : {}),
        ...(cacheWriteTokens !== undefined ? { cacheWriteTokens } : {}),
    }
}
